import _ from 'underscore';
import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { SiteConfigContext } from '@bingads-webui-campaign-react/smart-page-context';
import {
  getComponentById,
  getComponentByType,
  DATA_SOURCE_KEY_MAPPING,
  COMPONENT_TYPE_MAPPING,
  useDefaultEditorStyle,
} from '@bingads-webui-campaign-react/smart-component-util';

import { Mode } from './data-config';
import { SmartHoursEditor } from './smart-hours-editor';
import { proxyReadHoursConfig } from './proxy-read-hours-config';

export const SmartBusinessHoursEditorWrapper = ({
  i18n,
  componentId,
  isInContact,
  showHeaderToggle,
  headerToggleChecked,
  onHeaderToggleChange,
  mode,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
  } = useDefaultEditorStyle();
  const {
    siteConfig,
    dispatchSiteConfig,
  } = useContext(SiteConfigContext);
  const [dataSourceCleaned, setDataSourceCleaned] = useState(false);

  const { hours, fromDs } = proxyReadHoursConfig(siteConfig, componentId);

  // Update date change into SiteConfig
  const onTimeChange = useCallback((updatedHours) => {
    const componentConfig = getComponentById(siteConfig, componentId);

    const update = {
      [DATA_SOURCE_KEY_MAPPING.BusinessHours]: updatedHours,
    };

    // Update Business Hours in SiteConfig
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: {
        ...componentConfig,
        ...update,
      },
    });

    // When importing from FB, the business hours data is stored in Datasource,
    // need to remove this after it's been stored in SiteConfig
    if (fromDs && !dataSourceCleaned) {
      dispatchSiteConfig({
        type: 'UPDATE_DATASOURCE_BY_NAME',
        payload: {
          name: DATA_SOURCE_KEY_MAPPING.BusinessHours,
          value: null,
        },
      });
      setDataSourceCleaned(true);
    }

    // If this component is renderred inside the independent Business Hours component,
    // make sure the update is also patched into Contact Component.
    // TODO: After GA, remove this logic
    if (!isInContact) {
      const contactComponentConfig = getComponentByType(siteConfig, COMPONENT_TYPE_MAPPING.Contact);
      dispatchSiteConfig({
        type: 'UPDATE_COMPONENT_BY_ID',
        payload: {
          ...contactComponentConfig,
          ...update,
          showHours: true,
        },
      });
    }
  }, [dataSourceCleaned, dispatchSiteConfig, fromDs, componentId, isInContact, siteConfig]);

  const businessHoursEditor = (
    <SmartHoursEditor
      i18n={i18n}
      id={componentId}
      showHeaderToggle={showHeaderToggle}
      headerToggleChecked={headerToggleChecked}
      onHeaderToggleChange={onHeaderToggleChange}
      time={hours}
      onTimeChange={onTimeChange}
      mode={mode}
    />
  );

  if (isInContact) {
    return businessHoursEditor;
  }

  return (
    <div>
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Business hours'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Set your business hours to let potential customers know when you\'re open.'))}
      </div>
      <div className={editorContentClassName}>
        {businessHoursEditor}
      </div>
    </div>
  );
};

SmartBusinessHoursEditorWrapper.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
  isInContact: PropTypes.bool,
  showHeaderToggle: PropTypes.bool,
  headerToggleChecked: PropTypes.bool,
  onHeaderToggleChange: PropTypes.func,
  mode: PropTypes.string,
};

SmartBusinessHoursEditorWrapper.defaultProps = {
  isInContact: false,
  showHeaderToggle: false,
  headerToggleChecked: true,
  onHeaderToggleChange: _.noop,
  mode: Mode.VERTICAL,
};
